import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  title: "",
  content: "",
  excerpt: "",
  type: "story",
  tags: [],
  status: "draft",
};

export default function AdminEditor() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        const { data } = await api.get(`/admin/posts/${id}`);
        setForm({
          title: data.title,
          content: data.content,
          excerpt: data.excerpt || "",
          type: data.type,
          tags: data.tags || [],
          status: data.status,
        });
      } catch (e) {
        setError("Could not load this piece.");
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [id]);

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || form.tags.includes(t)) {
      setTagInput("");
      return;
    }
    setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  };

  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  const save = async (status) => {
    setError("");
    setSaving(true);

    // Commit any pending tag still sitting in the input (user clicked Save
    // without pressing Enter). React state updates are async, so we compute
    // the final tags list synchronously here and use it directly.
    const pending = tagInput.trim().toLowerCase();
    let tagsToSave = form.tags;
    if (pending && !tagsToSave.includes(pending)) {
      tagsToSave = [...tagsToSave, pending];
    }

    const payload = {
      ...form,
      tags: tagsToSave,
      status: status || form.status,
    };
    try {
      let data;
      if (id) {
        const res = await api.put(`/admin/posts/${id}`, payload);
        data = res.data;
      } else {
        const res = await api.post("/admin/posts", payload);
        data = res.data;
      }
      setTagInput("");
      setForm({
        title: data.title,
        content: data.content,
        excerpt: data.excerpt || "",
        type: data.type,
        tags: data.tags,
        status: data.status,
      });
      if (!id) navigate(`/admin/edit/${data.id}`, { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || !user || !loaded) {
    return (
      <p className="font-display italic text-[#5C554E] text-center py-24">Preparing the desk…</p>
    );
  }

  return (
    <div className="paper-grain min-h-[80vh]" data-testid="admin-editor">
      <div className="max-w-3xl mx-auto px-6 pt-10 pb-20">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-[#D9D2C7]">
          <Link to="/admin" className="btn-link" data-testid="editor-back">
            ← Back to Desk
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2" data-testid="editor-type-toggle">
              {["story", "poem"].map((t) => (
                <button
                  key={t}
                  onClick={() => setForm({ ...form, type: t })}
                  className="tag-pill"
                  data-active={form.type === t}
                  data-testid={`editor-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <button
              onClick={() => save("draft")}
              disabled={saving || !form.title.trim()}
              className="btn-ghost"
              data-testid="editor-save-draft"
            >
              {saving ? "Saving…" : "Save Draft"}
            </button>
            <button
              onClick={() => save("published")}
              disabled={saving || !form.title.trim()}
              className="btn-solid"
              data-testid="editor-publish"
            >
              {form.status === "published" ? "Update" : "Publish"}
            </button>
          </div>
        </div>

        {error && (
          <p className="text-[#C45543] font-ui text-sm mt-4" data-testid="editor-error">
            {error}
          </p>
        )}

        {/* Title */}
        <div className="mt-10">
          <p className="overline mb-2">Title</p>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Give it a name…"
            className="editor-title"
            data-testid="editor-title-input"
          />
        </div>

        {/* Excerpt */}
        <div className="mt-8">
          <p className="overline mb-2">Excerpt (optional)</p>
          <textarea
            rows={2}
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            placeholder="A single line to entice the reader…"
            className="input-literary italic"
            style={{ resize: "vertical" }}
            data-testid="editor-excerpt-input"
          />
        </div>

        {/* Tags */}
        <div className="mt-8">
          <p className="overline mb-2">Tags</p>
          <div className="flex flex-wrap items-center gap-2">
            {form.tags.map((t) => (
              <button
                key={t}
                onClick={() => removeTag(t)}
                className="tag-pill"
                data-active="true"
                title="Remove"
                data-testid={`editor-tag-${t}`}
              >
                {t} ×
              </button>
            ))}
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addTag();
                }
              }}
              onBlur={addTag}
              placeholder="add a tag"
              className="input-literary"
              style={{ width: 160 }}
              data-testid="editor-tag-input"
            />
          </div>
        </div>

        {/* Content */}
        <div className="mt-10">
          <p className="overline mb-2">
            {form.type === "poem" ? "Verse (line breaks preserved)" : "Prose (blank line = new paragraph)"}
          </p>
          <textarea
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder={
              form.type === "poem"
                ? "A poem unfolds, line by patient line…"
                : "Begin in the middle, or wherever the door is open…"
            }
            className="editor-textarea"
            data-testid="editor-content-input"
          />
        </div>

        <p className="overline mt-6" data-testid="editor-status-label">
          Status: {form.status}
        </p>
      </div>
    </div>
  );
}
